/**
 * HeaderContext for UI-Model-1 (API-Direct Approach)
 *
 * Thin wrapper context that:
 * - Reads header data from UIModelStateContext (state.model.layout.header)
 * - Dispatches updates via UIModelStateContext
 * - Provides domain-specific convenience methods
 *
 * Line limit: ~80 lines
 */

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { HeaderConfigAPI, DEFAULT_HEADER_CONFIGURATION } from '../types/state.types';
import { HeaderContextValue } from '../types/context.types';
import { useUIModelState } from './UIModelStateContext';

// ============================================================================
// Context
// ============================================================================

const HeaderContext = createContext<HeaderContextValue | null>(null);

export const HeaderProvider = ({ children }: { children: React.ReactNode }) => {
  const { state, dispatch } = useUIModelState();

  // ============================================================================
  // Derived State (from UIModelState)
  // ============================================================================

  const header = useMemo<HeaderConfigAPI | null>(() => {
    if (!state.model?.layout?.header) return null;
    return state.model.layout.header;
  }, [state.model?.layout?.header]);

  // ============================================================================
  // Update Operations
  // ============================================================================

  const updateHeader = useCallback((updates: Partial<HeaderConfigAPI['configuration']>) => {
    if (!state.model) return;

    const currentHeader = state.model.layout?.header || DEFAULT_HEADER_CONFIGURATION;

    const newModel = {
      ...state.model,
      layout: {
        ...state.model.layout,
        header: {
          ...currentHeader,
          configuration: {
            ...currentHeader.configuration,
            ...updates,
          },
        },
      },
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'header' } });
  }, [state.model, dispatch]);

  const resetHeader = useCallback(() => {
    if (!state.model) return;

    const newModel = {
      ...state.model,
      layout: {
        ...state.model.layout,
        header: DEFAULT_HEADER_CONFIGURATION,
      },
    };

    dispatch({ type: 'SET_MODEL', payload: newModel });
    dispatch({ type: 'MARK_DIRTY', payload: { section: 'header' } });
  }, [state.model, dispatch]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value = useMemo<HeaderContextValue>(
    () => ({
      header,
      updateHeader,
      resetHeader,
    }),
    [header, updateHeader, resetHeader]
  );

  return (
    <HeaderContext.Provider value={value}>
      {children}
    </HeaderContext.Provider>
  );
};

export const useHeader = (): HeaderContextValue => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

export default HeaderContext;
